import { useId, useState, type ReactNode } from 'react';
import { visibleSettings, type GameId, type GameSettingDef, type GameSettings } from '@hearth/shared';
import { choiceLabel } from './serverWords';

// A game's settings on the create page (and, with PR 6, the Settings tab), as the canvas boards: two
// collapsed sections, World options (fresh worlds only) and Game settings, each showing its choice
// while closed. Labels and options come from GAME_SETTINGS; the pixel icons are Minecraft's (a second
// game brings its own fields).

type Rect = readonly [x: number, y: number, w: number, h: number, fill: string];

/** Decorative pixel icons by setting value (original art, as the campfire). */
const ICONS: Record<string, { w: number; h: number; rects: readonly Rect[] }> = {
  peaceful: { w: 7, h: 8, rects: [[2, 0, 3, 1, '#79b35c'], [1, 1, 5, 2, '#5f9e3a'], [2, 3, 3, 1, '#5f9e3a'], [3, 4, 1, 3, '#8a5a36'], [1, 7, 5, 1, '#6b4428']] },
  easy: { w: 7, h: 6, rects: [[1, 0, 2, 1, '#ef6b5b'], [4, 0, 2, 1, '#ef6b5b'], [0, 1, 7, 2, '#ef6b5b'], [1, 3, 5, 1, '#ef6b5b'], [2, 4, 3, 1, '#ef6b5b'], [3, 5, 1, 1, '#ef6b5b'], [1, 1, 1, 1, '#ffb3a8']] },
  normal: { w: 8, h: 8, rects: [[7, 0, 1, 1, '#ece6da'], [6, 1, 1, 1, '#c9ccd3'], [5, 2, 1, 1, '#c9ccd3'], [4, 3, 1, 1, '#c9ccd3'], [3, 4, 1, 1, '#c9ccd3'], [1, 3, 1, 1, '#8a5a36'], [2, 4, 1, 1, '#8a5a36'], [3, 5, 1, 1, '#8a5a36'], [4, 6, 1, 1, '#8a5a36'], [2, 5, 1, 1, '#6b4428'], [1, 6, 1, 1, '#6b4428'], [0, 7, 1, 1, '#ffc46b']] },
  hard: { w: 7, h: 7, rects: [[1, 0, 5, 1, '#ece6da'], [0, 1, 7, 3, '#ece6da'], [1, 4, 5, 1, '#ece6da'], [2, 5, 3, 1, '#ece6da'], [2, 6, 1, 1, '#ece6da'], [4, 6, 1, 1, '#ece6da'], [1, 2, 2, 2, '#0d1018'], [4, 2, 2, 2, '#0d1018'], [3, 4, 1, 1, '#0d1018']] },
  survival: { w: 8, h: 8, rects: [[1, 0, 6, 1, '#9aa3b5'], [0, 1, 1, 2, '#9aa3b5'], [7, 1, 1, 2, '#9aa3b5'], [1, 0, 1, 1, '#c9ccd3'], [3, 1, 2, 7, '#8a5a36'], [3, 1, 1, 7, '#a8754a']] },
  creative: { w: 8, h: 8, rects: [[2, 0, 4, 4, '#c08a4a'], [2, 0, 4, 1, '#d9a764'], [0, 4, 4, 4, '#8a5a36'], [0, 4, 4, 1, '#5f9e3a'], [4, 4, 4, 4, '#6f7889'], [4, 4, 4, 1, '#9aa3b5']] },
  adventure: { w: 4, h: 8, rects: [[1, 0, 2, 1, '#ffc46b'], [0, 1, 4, 2, '#ff8a3d'], [1, 1, 2, 1, '#fff1c9'], [1, 3, 2, 1, '#b65316'], [1, 4, 2, 4, '#8a5a36'], [2, 4, 1, 4, '#6b4428']] },
};

function Pixels({ w, h, rects, width, height, className }: { w: number; h: number; rects: readonly Rect[]; width: number; height: number; className?: string }) {
  return (
    <svg className={className} width={width} height={height} viewBox={`0 0 ${w} ${h}`} shapeRendering="crispEdges" preserveAspectRatio="none" aria-hidden="true">
      {rects.map(([x, y, rw, rh, fill], i) => <rect key={i} x={x} y={y} width={rw} height={rh} fill={fill} />)}
    </svg>
  );
}

export function SettingIcon({ value, height }: { value: string; height: number }) {
  const icon = ICONS[value];
  return icon ? <Pixels {...icon} width={Math.round((icon.w / icon.h) * height)} height={height} className="setting-icon" /> : null;
}

/** Each world type as 16 columns of land on a night sky. */
const TERRAIN: Record<string, { heights: readonly number[]; top: (i: number, h: number) => string; body: string }> = {
  normal: { heights: [4, 4, 5, 5, 6, 6, 5, 4, 4, 3, 3, 4, 5, 5, 4, 4], top: () => '#5f9e3a', body: '#8a5a36' },
  flat: { heights: Array<number>(16).fill(2), top: () => '#5f9e3a', body: '#8a5a36' },
  largeBiomes: { heights: [4, 4, 4, 4, 4, 5, 5, 5, 4, 3, 3, 3, 3, 3, 3, 3], top: (i) => (i < 8 ? '#5f9e3a' : '#e3c87a'), body: '#8a5a36' },
  amplified: { heights: [3, 7, 8, 5, 2, 6, 9, 8, 3, 5, 8, 6, 2, 4, 7, 5], top: (_, h) => (h >= 7 ? '#ece6da' : '#5f9e3a'), body: '#6f7889' },
};

export function WorldThumb({ type, width, height }: { type: string; width: number; height: number }) {
  const t = TERRAIN[type];
  if (!t) return null;
  const land = t.heights.flatMap((h, i): Rect[] => [[i, 10 - h, 1, 1, t.top(i, h)], [i, 11 - h, 1, h - 1, t.body]]);
  return <Pixels w={16} h={10} rects={[[0, 0, 16, 10, '#1d2740'], [3, 1, 1, 1, '#ece6da'], [12, 2, 1, 1, '#9aa3b5'], ...land]} width={width} height={height} className="world-thumb" />;
}

const def = (game: GameId, key: string): GameSettingDef | undefined => visibleSettings(game).find((d) => d.key === key);

/** What to send: only the settings that differ from the defaults, and world settings only for a new world. */
export function changedSettings(game: GameId, s: GameSettings, newWorld: boolean): GameSettings {
  return Object.fromEntries(visibleSettings(game).filter((d) => (newWorld || d.when === 'wake') && s[d.key] !== undefined && s[d.key] !== d.default).map((d) => [d.key, s[d.key]!]));
}

/** A random seed, as Minecraft would make (any whole number fits). */
export const randomSeed = () => `${Math.random() < 0.5 ? '-' : ''}${Math.floor(Math.random() * 9e15)}`;

const Chevron = ({ open }: { open: boolean }) => (
  <svg className={open ? 'chev open' : 'chev'} width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="m6 9 6 6 6-6" />
  </svg>
);

/** World options: closed, the world type; open, the type as thumbnails, the seed and structures. */
export function WorldOptions({ game, settings, onChange, disabled }: { game: GameId; settings: GameSettings; onChange: (s: GameSettings) => void; disabled?: boolean }) {
  const [open, setOpen] = useState(false);
  const id = useId();
  const types = def(game, 'worldType');
  const seed = def(game, 'seed');
  const set = (key: string, value: string | boolean) => onChange({ ...settings, [key]: value });
  return (
    <div className="options">
      <button className={open ? 'disclosure open' : 'disclosure'} type="button" aria-expanded={open} aria-controls={id} onClick={() => setOpen(!open)}>
        World options
        <span className="disclosure-value">
          <WorldThumb type={String(settings.worldType)} width={32} height={20} />
          {choiceLabel(game, 'worldType', settings.worldType)}
        </span>
        <Chevron open={open} />
      </button>
      {open && (
        <div id={id} className="drawer">
          <p className="option-label" id={`${id}-type`}>World type</p>
          <div className="thumbs" role="radiogroup" aria-labelledby={`${id}-type`}>
            {types?.kind === 'choice' &&
              types.options.map((o) => (
                <button key={o.value} className={settings.worldType === o.value ? 'thumb on' : 'thumb'} type="button" role="radio" aria-checked={settings.worldType === o.value} disabled={disabled} onClick={() => set('worldType', o.value)}>
                  <WorldThumb type={o.value} width={160} height={64} />
                  {o.label}
                  {o.note && <span className="thumb-note">{o.note}</span>}
                </button>
              ))}
          </div>
          <label className="option-label" htmlFor={`${id}-seed`}>Seed</label>
          <span className="seed-row">
            <input id={`${id}-seed`} className="field" type="text" placeholder="Random" maxLength={seed?.kind === 'text' ? seed.maxLength : undefined} value={String(settings.seed ?? '')} disabled={disabled} onChange={(e) => set('seed', e.target.value)} />
            <button className="btn" type="button" disabled={disabled} onClick={() => set('seed', randomSeed())}>Roll</button>
          </span>
          <label className="check">
            <input type="checkbox" checked={settings.structures === true} disabled={disabled} onChange={(e) => set('structures', e.target.checked)} />
            <span>{def(game, 'structures')?.label}</span>
          </label>
        </div>
      )}
    </div>
  );
}

/**
 * Game settings: a panel whose heading opens it; closed, "⛏ Survival · 🗡 Normal". Open: the mode, the
 * difficulty as a slider (Hardcore pins it at Hard), and Hardcore for a new world.
 */
export function GameSettingsPanel({ game, title, settings, onChange, newWorld, note, disabled }: {
  game: GameId;
  title: string;
  settings: GameSettings;
  onChange: (s: GameSettings) => void;
  newWorld: boolean;
  note: ReactNode;
  disabled?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const id = useId();
  const modes = def(game, 'mode');
  const levels = def(game, 'difficulty');
  const options = levels?.kind === 'choice' ? levels.options : [];
  const hardcore = settings.hardcore === true;
  const difficulty = hardcore ? 'hard' : String(settings.difficulty);
  const index = Math.max(0, options.findIndex((o) => o.value === difficulty));
  const set = (key: string, value: string | boolean) => onChange({ ...settings, [key]: value });
  return (
    <section className="panel">
      <button className="panel-toggle" type="button" aria-expanded={open} aria-controls={id} onClick={() => setOpen(!open)}>
        <h2>{title}</h2>
        <span className="disclosure-value">
          <SettingIcon value={String(settings.mode)} height={16} />
          {`${choiceLabel(game, 'mode', settings.mode)} · `}
          <SettingIcon value={difficulty} height={15} />
          {hardcore ? 'Hardcore' : choiceLabel(game, 'difficulty', difficulty)}
        </span>
        <Chevron open={open} />
      </button>
      {open && (
        <div id={id} className="options-body">
          <p className="option-label" id={`${id}-mode`}>Game mode</p>
          <div className="pills" role="radiogroup" aria-labelledby={`${id}-mode`}>
            {modes?.kind === 'choice' &&
              modes.options.map((o) => (
                <button key={o.value} className={settings.mode === o.value ? 'pill on' : 'pill'} type="button" role="radio" aria-checked={settings.mode === o.value} disabled={disabled} onClick={() => set('mode', o.value)}>
                  <SettingIcon value={o.value} height={16} />
                  {o.label}
                </button>
              ))}
          </div>
          <label className="option-label" htmlFor={`${id}-difficulty`}>Difficulty</label>
          <div className="difficulty">
            <div className="difficulty-row" aria-hidden="true">
              {options.map((o, i) => <span key={o.value} className={i === index ? 'difficulty-icon on' : 'difficulty-icon'}><SettingIcon value={o.value} height={22} /></span>)}
            </div>
            <input
              id={`${id}-difficulty`}
              className={hardcore ? 'hardcore' : undefined}
              type="range"
              min={0}
              max={options.length - 1}
              step={1}
              value={index}
              aria-valuetext={hardcore ? 'Hardcore' : options[index]?.label}
              // Hardcore pins it at Hard: locked but not greyed out (the browser greys a disabled range), so the track shows red.
              aria-disabled={hardcore || undefined}
              disabled={disabled}
              onChange={(e) => {
                if (!hardcore) set('difficulty', options[Number(e.target.value)]?.value ?? difficulty);
              }}
            />
            <div className="difficulty-row" aria-hidden="true">
              {options.map((o, i) => <span key={o.value} className={i === index ? 'on' : undefined}>{o.label}</span>)}
            </div>
          </div>
          {newWorld && (
            <label className="check">
              <input type="checkbox" checked={hardcore} disabled={disabled} onChange={(e) => set('hardcore', e.target.checked)} />
              <span className="check-lines">
                <span>{def(game, 'hardcore')?.label}</span>
                <span className="quiet">{def(game, 'hardcore')?.help}</span>
              </span>
            </label>
          )}
          <p className="quiet">{note}</p>
        </div>
      )}
    </section>
  );
}

/**
 * A server's world settings, read only (the Settings tab): set when the world was made, so they can't
 * change. Only a world made here has them (an uploaded one keeps its own, unknown to Hearth).
 */
export function WorldPanel({ game, stored }: { game: GameId; stored: GameSettings }) {
  const [open, setOpen] = useState(false);
  const id = useId();
  const onOff = (value: unknown) => (value === true ? 'On' : 'Off');
  return (
    <section className="panel">
      <button className="panel-toggle" type="button" aria-expanded={open} aria-controls={id} onClick={() => setOpen(!open)}>
        <h2>World options</h2>
        <span className="disclosure-value">
          <WorldThumb type={String(stored.worldType)} width={32} height={20} />
          {choiceLabel(game, 'worldType', stored.worldType)}
          {stored.hardcore === true && ' · Hardcore'}
        </span>
        <Chevron open={open} />
      </button>
      {open && (
        <div id={id} className="options-body">
          <dl className="world-facts">
            {[
              ['World type', choiceLabel(game, 'worldType', stored.worldType)],
              ['Seed', String(stored.seed ?? '') || 'Random'],
              ['Structures', onOff(stored.structures ?? true)],
              ['Hardcore', onOff(stored.hardcore)],
            ].map(([label, value]) => (
              <div key={label}>
                <dt>{label}</dt>
                <dd>{value}</dd>
              </div>
            ))}
          </dl>
          <p className="quiet">Set when the world was made, so these can't change.</p>
        </div>
      )}
    </section>
  );
}
